import { Cookies } from 'react-cookie';

export const ATTRIBUTION_COOKIE_NAME = 'qc_attribution';

// Last-touch click-source param the marketing site stamps on app links; maps to SignupAttribution.signup_channel.
const CHANNEL_PARAM = 'qc_channel';

const URL_ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'twclid',
  'ttclid',
] as const;

// Mirrors SIGNUP_ATTRIBUTION_SESSION_FIELDS in appquickchat/analytics/attribution.py.
export const ATTRIBUTION_FIELDS = [
  ...URL_ATTRIBUTION_PARAMS,
  'oppref',
  'ga_client_id',
  'referrer',
  'landing_path',
  'signup_channel',
  'first_seen_at',
] as const;

export type SignupAttribution = Record<string, string>;

// Any .quickchat.ai subdomain can write the cookie, so take only known string fields — an arbitrary key would otherwise ride onto the OAuth init URL.
const readCookieAttribution = (cookies: Cookies): SignupAttribution => {
  const payload = cookies.get(ATTRIBUTION_COOKIE_NAME);
  if (!payload || typeof payload !== 'object') return {};
  const out: SignupAttribution = {};
  ATTRIBUTION_FIELDS.forEach((key) => {
    const value = (payload as Record<string, unknown>)[key];
    if (typeof value === 'string' && value) out[key] = value;
  });
  return out;
};

// Ad pixels set their own cookies; read them at signup since they may post-date the first touch.
const readPlatformCookies = (cookies: Cookies): SignupAttribution => {
  const out: SignupAttribution = {};
  const oppref = cookies.get('__oppref', { doNotParse: true });
  if (oppref) out.oppref = String(oppref);
  const ga = cookies.get('_ga', { doNotParse: true });
  if (ga) {
    // `_ga` is "GA1.<scope>.<clientId>"; the GA4 client id is the trailing "X.Y".
    const clientId = String(ga).split('.').slice(2).join('.');
    if (clientId) out.ga_client_id = clientId;
  }
  return out;
};

// First-touch cookie (set on .quickchat.ai by the marketing site) wins; URL params and
// freshly-read pixel cookies fill any gaps.
export const readSignupAttribution = (): SignupAttribution | undefined => {
  const cookies = new Cookies();
  const cookieData = readCookieAttribution(cookies);

  const params = new URLSearchParams(window.location.search);
  const urlData: SignupAttribution = {};
  URL_ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) urlData[key] = value;
  });
  const channel = params.get(CHANNEL_PARAM);
  if (channel) urlData.signup_channel = channel;

  const attribution = { ...urlData, ...readPlatformCookies(cookies), ...cookieData };
  return Object.keys(attribution).length > 0 ? attribution : undefined;
};

// Attribution rides on the OAuth init URL because the provider round-trip drops our URL params; the backend replays it from the session (SOCIAL_AUTH_FIELDS_STORED_IN_SESSION).
export const withSignupAttribution = (url: string): string => {
  try {
    if (new URL(url, window.location.origin).origin !== window.location.origin) return url;
  } catch {
    return url;
  }
  const query = new URLSearchParams(readSignupAttribution() ?? {}).toString();
  if (!query) return url;
  const hashAt = url.indexOf('#');
  const [base, fragment] = hashAt === -1 ? [url, ''] : [url.slice(0, hashAt), url.slice(hashAt)];
  return `${base}${base.includes('?') ? '&' : '?'}${query}${fragment}`;
};
